import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useForm } from 'react-final-form';
import strings from 'localization/strings';
import { formatShortDate, getShortDateFormat } from 'helpers/DateHelper';
import { startOfDay, sub, add } from 'date-fns';
import { validateTimePeriodPriceCollectionFormData } from 'logic/timePeriodPriceLogic';

import TimePeriodPriceEditor from './TimePeriodPriceEditor';
import Button from '@material-ui/core/Button';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import Alert from '@material-ui/lab/Alert';
import MarginBox from 'common/MarginBox';
import NullableDatePickerDialog from 'components/NullableDatePickerDialog';

const TimePeriodPricesEditor = ({ fieldNamePrefix, amountLabel, currency, referenceDictionary, validateFullCoverage = true, vatRateVisible, eligibleForVatVisible, commissionRateVisible, timePeriodVisible, linkIconVisible, commissionRateEnabled = true }) => {
    const form = useForm();
    const { appContext } = useAppContext();
    const getTomorrow = () => startOfDay(add(new Date(), { days: 1 }));

    const [ datePickerDialogOpen, setDatePickerDialogOpen ] = useState(false);

    const timePeriodPriceFieldArray = useFieldArray(fieldNamePrefix);
    const timePeriodPriceCollectionFormData = timePeriodPriceFieldArray.fields.value;
    const timePeriodPricesErrorMessage = timePeriodPriceCollectionFormData && validateFullCoverage
        ? validateTimePeriodPriceCollectionFormData(timePeriodPriceCollectionFormData, appContext, validateFullCoverage)
        : undefined;

    const handleAddTimePeriodPrice = () => {
        setDatePickerDialogOpen(true);
    };

    const handleRemoveTimePeriodPrice = (fields, index) => {
        fields.remove(index);
    };

    const handleDatePickerDialogOk = value => {
        const fields = timePeriodPriceFieldArray.fields;
        const previous = fields.value[fields.value.length - 1];
        setDatePickerDialogOpen(false);
        if(previous.endDateEditable) {
            form.mutators.changeValue({ name: `${fields.name}[${fields.value.length - 1}].endDate`, value: formatShortDate(sub(value, { days: 1 }), appContext) });
        }
        fields.push({
            amount: null,
            vatRate: previous.vatRate,
            eligibleForVat: previous.eligibleForVat,
            commissionRate: previous.commissionRate,
            startDate: formatShortDate(value, appContext),
            endDate: null,
            inUse: false,
            editable: true,
            endDateEditable: true,
            isNew: true
        });
    };

    const handleDatePickerDialogCancel = () => {
        setDatePickerDialogOpen(false);
    };

    return (
        <>
            <FieldArray name={fieldNamePrefix}>
                {({ fields }) => (
                    <>
                        {
                            fields.map((name, index) => (
                                <TimePeriodPriceEditor
                                    key={index}
                                    fieldNamePrefix={name}
                                    amountLabel={amountLabel}
                                    currency={currency}
                                    referenceDictionary={referenceDictionary}
                                    vatRateVisible={vatRateVisible}
                                    eligibleForVatVisible={eligibleForVatVisible}
                                    commissionRateVisible={commissionRateVisible}
                                    timePeriodVisible={timePeriodVisible}
                                    linkIconVisible={linkIconVisible}
                                    commissionRateEnabled={commissionRateEnabled}
                                    canRemove={fields.length > 1}
                                    onRemove={() => handleRemoveTimePeriodPrice(fields, index)}
                                />
                            ))
                        }
                        {
                            timePeriodVisible &&
                            (
                                <MarginBox bottom={2}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={handleAddTimePeriodPrice}
                                    >
                                        {strings.addTimePeriodPrice}
                                    </Button>
                                </MarginBox>
                            )
                        }
                        {
                            timePeriodPricesErrorMessage &&
                            (
                                <MarginBox bottom={2}>
                                    <Alert severity="error">
                                        {timePeriodPricesErrorMessage}
                                    </Alert>
                                </MarginBox>
                            )
                        }
                    </>
                )}
            </FieldArray>

            <NullableDatePickerDialog
                open={datePickerDialogOpen}
                title={strings.startDate}
                description={strings.selectTimePeriodStartDate}
                formatFunc={getShortDateFormat}
                initialValue={getTomorrow()}
                onOk={handleDatePickerDialogOk}
                onCancel={handleDatePickerDialogCancel}
                inputVariant="outlined"
                nullButtonLabel={strings.fromBeginning}
                nullString={strings.fromBeginning}
            />
        </>
    );
};

TimePeriodPricesEditor.propTypes = {
    fieldNamePrefix: PropTypes.string.isRequired,
    amountLabel: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    referenceDictionary: PropTypes.object,
    validateFullCoverage: PropTypes.bool,
    vatRateVisible: PropTypes.bool,
    eligibleForVatVisible: PropTypes.bool,
    commissionRateVisible: PropTypes.bool,
    timePeriodVisible: PropTypes.bool,
    linkIconVisible: PropTypes.bool,
    commissionRateEnabled: PropTypes.bool
};

export default TimePeriodPricesEditor;
